import styled, { css } from 'styled-components'
import { tokens } from '../../styles/tokens'

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost'
type Size = 'sm' | 'md'

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
}

const variants = {
  primary: css`
    background: linear-gradient(135deg, ${tokens.purple600}, ${tokens.purple400});
    color: white;
    border: none;
    box-shadow: 0 4px 12px rgba(107, 58, 138, 0.25);
    &:hover:not(:disabled) {
      opacity: 0.9;
      transform: translateY(-1px);
    }
  `,
  secondary: css`
    background: white;
    color: ${tokens.purple600};
    border: 1px solid ${tokens.purple100};
    &:hover:not(:disabled) {
      background: ${tokens.purple50};
    }
  `,
  danger: css`
    background: white;
    color: #c0436a;
    border: 1px solid #f5c0d0;
    &:hover:not(:disabled) {
      background: #fdeef4;
    }
  `,
  ghost: css`
    background: none;
    color: ${tokens.purple400};
    border: none;
    &:hover:not(:disabled) {
      color: ${tokens.purple600};
    }
  `,
}

const sizes = {
  sm: css`
    padding: 6px 12px;
    font-size: 12px;
  `,
  md: css`
    padding: 10px 18px;
    font-size: 13px;
  `,
}

const StyledButton = styled.button<{ $variant: Variant; $size: Size }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  ${p => variants[p.$variant]}
  ${p => sizes[p.$size]}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  children,
  disabled,
  ...rest
}: Props) {
  return (
    <StyledButton $variant={variant} $size={size} disabled={disabled || loading} {...rest}>
      {loading ? 'Loading…' : children}
    </StyledButton>
  )
}
